import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authenticate, fetchMe, logout as logoutRequest, session } from './api';
import type { AuthUser } from './types';

interface AuthValue { user: AuthUser | null; loading: boolean; login: (u: string, p: string) => Promise<void>; register: (u: string, p: string) => Promise<void>; logout: () => Promise<void> }
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    if (!session.get()) { setLoading(false); return; }
    fetchMe().then((value) => { if (active) setUser(value); }).catch(() => session.clear()).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  const complete = async (mode: 'login' | 'register', username: string, password: string) => {
    const data = await authenticate(mode, username, password); session.set(data.token); setUser(data.user);
  };
  const value = useMemo<AuthValue>(() => ({ user, loading, login: (u, p) => complete('login', u, p), register: (u, p) => complete('register', u, p), logout: async () => { try { await logoutRequest(); } finally { session.clear(); setUser(null); } } }), [user, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() { const value = useContext(AuthContext); if (!value) throw new Error('useAuth outside provider'); return value; }
